import {
  ref,
  Ref,
  computed,
  toRaw,
  watch,
  WatchOptions,
  ObjectDirective,
  effectScope,
  getCurrentScope
} from 'vue';
import { isPromise } from '@vue/shared';
import {
  computedAsync,
  useElementHover,
  useClipboard as useCoreClipboard
} from '@vueuse/core';
import {
  FormItemRule,
  CascaderOption,
  CascaderNodeValue,
  ElMessage,
  ElMessageBox,
  ElLoading,
  LoadingOptions
} from 'element-plus';
import { default as axios } from 'axios';
import { PlainObject } from './types';
import { IgnoreError, TipError, AlertError } from './Errors';

import { assertRestResponse } from './ApiProtocol';
import _ from 'lodash-es';

export { assertRestResponse };

export function showError(err: unknown) {
  if (err instanceof IgnoreError) {
    return false;
  }

  if (err instanceof AlertError) {
    const title = err.title ?? (err.warning ? '警告' : '错误');

    ElMessageBox.alert(
      err.message,
      title,
      {
        type: err.warning ? 'warning' : 'error',
        confirmButtonText: '关闭',
        closeOnClickModal: false,
        closeOnPressEscape: false,
        draggable: true
      },
      err.ctx
    );
    return false;
  }

  if (err instanceof TipError) {
    //const title = err.title ?? (err.warning ? '警告' : '错误');

    ElMessage({
      type: err.warning ? 'warning' : 'error',
      //title: title,
      message: err.message,
      showClose: false,
      duration: 2000
    });

    return false;
  }

  if ((err as any).isAxiosError) {
    console.debug(err);
    ElMessage({
      type: 'error',
      message: '网络错误',
      duration: 2000
    });

    return false;
  }

  if (err instanceof Error) {
    console.debug(err);
    ElMessage({
      type: 'error',
      message: err.message,
      duration: 2000
    });

    return false;
  }

  console.error('UnknownError', err);

  ElMessage({
    type: 'error',
    message: '未知错误',
    duration: 2000
  });
}

export function axiosRequest(
  url: string,
  method: 'post' | 'get',
  data?: PlainObject
) {
  const resp =
    method == 'get' ? axios.get(url, { params: data }) : axios.post(url, data);

  return resp
    .then((resp) => {
      return assertRestResponse(resp);
    })
    .catch((e) => {
      if (e.isAxiosError) {
        throw new AlertError(e.message);
      }

      throw e;
    });
}

export function axiosPost(url: string, data?: PlainObject) {
  return axios
    .post(url, data)
    .then((resp) => {
      return assertRestResponse(resp);
    })
    .catch((e) => {
      if (e.isAxiosError) {
        throw new AlertError(e.message);
      }

      throw e;
    });
}

// 下载文件
export function axiosDownload(
  url: string,
  requestData: PlainObject = {},
  forceFileName?: string
) {
  return axios
    .post(url, requestData, { responseType: 'blob' })
    .then((resp) => {
      const headers = resp.headers;
      const contentType = headers['content-type'];

      if (!resp.data) {
        console.error('响应异常：', resp);
        showError(new TipError(resp.statusText));
      } else {
        if (resp.data.type == 'application/json') {
          const reader = new FileReader();
          reader.onload = function () {
            const json = JSON.parse(reader.result as string);
            showError(
              new TipError(json.message || json.msg || '下载遇到未知错误')
            );
          };

          reader.readAsText(resp.data);
        } else {
          const blob = new Blob([resp.data], { type: contentType });

          const contentDisposition = resp.headers['content-disposition'];
          let fileName;
          if (forceFileName) {
            fileName = forceFileName;
          } else {
            fileName = 'unknown';
            if (contentDisposition) {
              fileName = window.decodeURI(
                resp.headers['content-disposition'].split('=')[1]
              );
            }
          }

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob); // 创建下载的链接
          link.download = fileName; // 下载后文件名
          link.click(); // 点击下载
          window.URL.revokeObjectURL(link.href); // 释放掉blob对象
        }
      }
    })
    .catch(showError);
}

// global transform scope
const globalScope = effectScope(true);

/**
 * 增强computedAsync
 */
type AsyncComputedOnCancel = (cancelCallback: Fn) => void;
interface AsyncComputedOptions {
  /**
   * Should value be evaluated lazily
   *
   * @default false
   */
  lazy?: boolean;

  manual?: boolean; // 手工调用load触发, 否则不会计算

  /**
   * Ref passed to receive the updated of async evaluation
   */
  evaluating?: Ref<boolean>;

  /**
   * 在全局scope中使用reactive
   */
  globalScope?: boolean;

  /**
   * Callback when error is caught.
   */
  onError?: (e: unknown) => void;

  //
  logInfo?: string;
}
type AsyncState = 'init' | 'running' | 'ok' | 'fail';
type Fn = () => void;
export type ComputedAsyncResult<T> = {
  data: Ref<T>;
  state: Ref<AsyncState>;
  loading: Ref<boolean>;
  cancel: Fn;
  load: (reload?: boolean, reset?: T) => void;
};

export function computedAsyncEx<T>(
  evaluationCallback: (
    abortController: AbortController,
    onCancel: AsyncComputedOnCancel
  ) => T | Promise<T>,
  initialState?: T,
  options?: AsyncComputedOptions
): ComputedAsyncResult<T> {
  const scope = options?.globalScope ? globalScope : getCurrentScope()!;
  return scope.run(() => {
    const loadCount = ref(0);
    const state = ref<AsyncState>('init');

    const loading = computed(
      () => state.value != 'ok' && state.value != 'fail'
    );

    // 取消函数
    let cancel: Fn = _.noop;

    const asyncRun = (callback: Fn) => Promise.resolve().then(callback);

    let counter = 0;
    let resetValue: T | undefined = undefined;

    const data = computedAsync(
      async (onCancel: AsyncComputedOnCancel) => {
        // 手工触发执行
        if (options?.manual && loadCount.value == 0) {
          return initialState as T;
        }

        // 重置值
        if (resetValue !== undefined && [loadCount.value]) {
          const tmp = resetValue;
          resetValue = undefined;

          asyncRun(() => ++loadCount.value);

          return tmp;
        }

        counter++;
        const counterAtBeginning = counter;

        const abortController = new AbortController();
        cancel = () => {
          abortController.abort();
          cancel = _.noop;
        };
        onCancel(cancel);

        const _onCancel = [loadCount.value] && onCancel;

        if (options?.logInfo) {
          console.info('computedAsyncEx: recomputing', options?.logInfo);
        }

        try {
          asyncRun(() => {
            if (counterAtBeginning == counter) state.value = 'running';
          });

          const result = await evaluationCallback(abortController, _onCancel);

          asyncRun(() => {
            if (counterAtBeginning == counter) state.value = 'ok';
          });
          return result;
        } catch (e: any) {
          asyncRun(() => {
            if (counterAtBeginning == counter) state.value = 'fail';
          });

          if (showError(e) !== false) {
            console.error('computedAsyncEx fail:', e);
            throw e;
          } else {
            return initialState as T;
          }
        }
      },
      initialState,
      options
    );

    let loaded = !options?.lazy;
    return {
      data,
      state,
      loading,
      cancel,
      load: (reload?: boolean, reset?: T) => {
        if (!reload && loaded) {
          return;
        }

        if (options?.logInfo) {
          console.info('computedAsyncEx: reload', options?.logInfo);
        }

        resetValue = reset;

        ++loadCount.value;
        // 访问data.value, 触发lazy computed计算
        loaded = [data.value] && true;
      }
    };
  })!;
}

let ifxElse = false;
/**
 * jsx工具函数
 * @param v
 * @param node
 * @returns
 */
export function ifx(v: any) {
  ifxElse = !v;
  if (v) return true;
  return '';
}

export function elseifx(v: any) {
  if (!ifxElse) return false;
  ifxElse = !v;

  if (v) return true;
  return '';
}

export function elsex() {
  return ifxElse;
}

// vue template中 带分割符的处理
let usedSplitter: string;
let outputSplitedCount: number;

export function setupSplitter(splitter: string, check: boolean) {
  usedSplitter = splitter;

  outputSplitedCount = 0;
  return check;
}

export function splittedText(content: string) {
  ++outputSplitedCount;
  return outputSplitedCount == 1 ? content : usedSplitter + content;
}

/**
 * 从list中提取field字段
 * @param list
 * @param field
 * @returns
 */
export function extract<T, F extends keyof T>(list: T[], field: F) {
  return list.map((i) => i[field]);
}

/**
 * 小数截断
 * @param v
 * @param n
 */
export function formatDecimal(v: string | number | undefined, n = 2): string {
  switch (typeof v) {
    case 'number': {
      return v.toFixed(n);
    }
    case 'string': {
      // 科学计数法
      if (v.indexOf('E') >= 0 || v.indexOf('e') >= 0) {
        return parseFloat(v).toFixed(n);
      }
    }
  }

  if (n == 0) {
    if (!v) return '0';
    const i = v.indexOf('.');
    if (i < 0) return v;
    return v.substring(0, i);
  }

  if (!v) {
    return '0.' + _.repeat('0', n);
  }

  const i = v.indexOf('.');
  if (i < 0) {
    return v + '.' + _.repeat('0', n);
  }

  const r = v.substring(i + 1);
  const l = r.length;
  if (l == n) {
    return v;
  }

  if (l < n) {
    return v + _.repeat('0', n - l);
  }

  return v.substring(0, i + n + 1);
}

export function toIntString(v: string | number | undefined) {
  return formatDecimal(v, 0);
}

export function arrayMove(array: any[], fromIndex: number, toIndex: number) {
  if (fromIndex == toIndex) return;

  array.splice(toIndex, 0, array.splice(fromIndex, 1)[0]);
}

interface ReactiveListener {
  chg?: (name: string, newValue: any, oldValue: any) => void;
  add?: (name: string, value: any) => void;
  del?: (name: string, value: any) => void;
}

export function watchReactive(
  r: PlainObject,
  listener: ReactiveListener,
  options?: WatchOptions
) {
  const oldValue = _.cloneDeep(toRaw(r));

  watch(
    r,
    (newValue) => {
      newValue = toRaw(newValue);

      const newKeys = Object.keys(newValue);

      for (const key of newKeys) {
        if (Object.hasOwn(newValue, key)) {
          // 值发现变化了
          if (!_.isEqual(newValue[key], oldValue[key])) {
            listener.chg?.(key, newValue[key], oldValue[key]);
            oldValue[key] = newValue[key];
          }
        } else {
          // 新出现的key
          listener.add?.(key, newValue[key]);
          oldValue[key] = newValue[key];
        }
      }

      const oldKeys = Object.keys(oldValue);
      for (const key of oldKeys) {
        if (!Object.hasOwn(oldValue, key)) {
          listener.del?.(key, oldValue[key]);
          delete oldValue[key];
        }
      }
    },
    options
  );
}

/**
 * 构造行数据
 * @param clazz 类
 * @param datas
 */
export function createRowData<T extends PlainObject>(
  ctor: new () => T,
  ...datas: (PlainObject | undefined)[]
): T {
  const tmp = new ctor();
  const res = {} as any;

  for (const data of datas) {
    if (!data) continue;

    for (const key of _.keys(tmp)) {
      if (Object.hasOwn(data, key) && !Object.hasOwn(res, key)) {
        res[key] = data[key];
      }
    }
  }

  // 未指定的字段, 使用class中的默认值
  for (const key of _.keys(tmp)) {
    if (!Object.hasOwn(res, key)) {
      res[key] = tmp[key];
    }
  }

  return res;
}

/**
 * 将对象的key 转成snake case, 即带下划线
 * @param o
 */
export function toSnakeObject(o?: PlainObject) {
  if (!o) return o;

  const s = {} as PlainObject;
  for (const [key, val] of Object.entries(o)) {
    s[_.snakeCase(key)] = val;
  }
  return s;
}

// 对象key forEach
export function forObj<T extends PlainObject, K extends keyof T, R>(
  o: T,
  cb: (key: K) => R | undefined
): R[] {
  const result: R[] = [];

  Object.keys(o).forEach((key) => {
    const r = cb(key as K);
    if (r !== undefined) result.push(r);
  });

  return result;
}

/**
 * 判断dom元素是否overflow
 * @param el
 */
/*export function isElementOverflow(el: HTMLElement) {
  return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
}*/

/**
 * 将文本放到剪切板
 * @param text 文本内容
 */
/*export function setClipboardText(text: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text);
    return;
  }

  const copy = function (e: any) {
    e.preventDefault();
    if (e.clipboardData) {
      e.clipboardData.setData('text/plain', text);
    } else if ((window as any).clipboardData) {
      (window as any).clipboardData.setData('text', text);
    }
  };

  window.addEventListener('copy', copy);
  document.execCommand('copy');
  window.removeEventListener('copy', copy);
}*/

// 显示Loading
export async function loading<T>(worker: () => T, options?: LoadingOptions) {
  const instance = ElLoading.service({
    background: '#ffffff55',
    ...(options || {})
  });

  try {
    return await worker();
  } finally {
    instance.close();
  }
}

export async function caught(worker: () => void, options?: LoadingOptions) {
  const instance = ElLoading.service(options);
  try {
    await worker();
  } catch (e) {
    instance.close();
    showError(e);
  } finally {
    instance.close();
  }
}

type PromiseRaw<R> = R extends Promise<infer T> ? T : never;

interface SubmitFunc<F extends (...args: any[]) => Promise<any>, D> {
  func: (...args: Parameters<F>) => ReturnType<F>;

  state: Ref<'pending' | 'running' | 'done'>;

  data: Ref<D>;
}

// 用于提交，增加loading状态
function submit<F extends (...args: any) => Promise<any>>(
  func: F
): SubmitFunc<F, PromiseRaw<ReturnType<F>> | undefined>;

function submit<
  F extends (...args: any) => Promise<any>,
  IV extends PromiseRaw<ReturnType<F>>
>(func: F, initValue: IV): SubmitFunc<F, PromiseRaw<ReturnType<F>>>;

function submit(
  func: (...args: any) => Promise<any>,
  initValue?: any
): SubmitFunc<(...args: any) => Promise<any>, any> {
  const state = ref<'pending' | 'running' | 'done'>('pending');

  const data = ref(initValue);

  return {
    func: async (...args: any) => {
      state.value = 'running';
      data.value = initValue;

      data.value = await func(...args);
      state.value = 'done';
      return data.value;
    },
    state,
    data
  };
}

export { submit };

/**
 * 异步值
 * @param func
 */
export function delayValue<T>(func: () => T): { value: T } {
  let val: { value: T };

  return new Proxy(
    {},
    {
      get(_, prop: string) {
        if (prop == 'value') {
          if (!val) {
            // 只调用一次
            val = { value: func() };
          }
          return val.value;
        }

        return undefined;
      }
    }
  ) as any;
}

/**
 * 构造element-plus的form验证函数
 * @param validate
 */
export function validator(validate: (value: any) => string | void): {
  validator: FormItemRule['validator'];
} {
  return {
    validator: (_rule, value) => {
      const res = validate(value);
      if (res && _.isString(res)) {
        return new Error(res);
      }
      return true;
    }
  };
}

/**
 * 构造element-plus的form验证函数
 * @param validate
 */
export function asyncValidator(
  validate: (value: any) => Promise<string | void>
): {
  asyncValidator: FormItemRule['asyncValidator'];
} {
  return {
    asyncValidator: async (_rule, value, callback) => {
      try {
        const res = await validate(value);
        if (res && _.isString(res)) {
          callback(new Error(res));
          return;
        }
      } catch (e) {
        callback(e as any);
        return;
      }
      callback();
    }
  };
}

/*export function getQueryString(params: Record<string, any>): string {
    const qs: string[] = [];

    const append = (key: string, value: any) => {
        qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    };

    const process = (key: string, value: any) => {
        if (isDefined(value)) {
            if (Array.isArray(value)) {
                value.forEach(v => {
                    process(key, v);
                });
            } else if (typeof value === 'object') {
                Object.entries(value).forEach(([k, v]) => {
                    process(`${key}[${k}]`, v);
                });
            } else {
                append(key, value);
            }
        }
    };

    Object.entries(params).forEach(([key, value]) => {
        process(key, value);
    });

    if (qs.length > 0) {
        return `?${qs.join('&')}`;
    }

    return '';
}*/

/**
 * 清空对象
 * @param obj
 */
export function emptyObject(obj: PlainObject) {
  for (const name of Object.keys(obj)) {
    Reflect.deleteProperty(obj, name);
  }
}

// 对象复制, 确保两个object key完全相同
export function copyObject(to: PlainObject, from: PlainObject) {
  for (const [key, val] of Object.entries(from)) {
    to[key] = val;
  }

  const toDeleteKeys = Object.keys(to).filter(
    (key) => !Object.hasOwn(from, key)
  );

  toDeleteKeys.forEach((key) => Reflect.deleteProperty(to, key));
}

/**
 * hover触发事件
 * @param callback
 * @param delay
 */
export function useHover(callback: () => void, delay = 500) {
  let timerId: number;
  const mouseenter = (immediate?: boolean) => {
    mouseleave();

    if (!immediate) {
      timerId = window.setTimeout(() => {
        timerId = 0;
        callback();
      }, delay);
    } else {
      callback();
    }
  };

  const mouseleave = () => {
    if (timerId) {
      window.clearTimeout(timerId);
      timerId = 0;
    }
  };

  return { mouseenter, mouseleave };
}

/**
 * 取wujie的主应用window
 */
// export function getWujieMainWindow(): Window {
//   let win = window as Window;

//   while (win.__WUJIE_RAW_WINDOW__) {
//     const parent = win.__WUJIE_RAW_WINDOW__.parent;
//     if (!parent || parent == win || !win.__WUJIE_RAW_WINDOW__) break;

//     win = parent;
//   }

//   return win;
// }

/**
 * 因为wujie没有代理navigator对象，导致vuecore/useClipboard不可用
 * 此函数用于修正
 */
export function useClipboard() {
  //   return useCoreClipboard({
  //     navigator: getWujieMainWindow().navigator
  //   });
  return useCoreClipboard();
}

// hover
type BindingValueFunction = (state: boolean) => void | Promise<void>;

export const vElementHover: ObjectDirective<HTMLElement, BindingValueFunction> =
  {
    mounted(el, binding) {
      if (typeof binding.value === 'function') {
        // 进入延时
        const delayEnter = parseInt(
          el.getAttribute('hover-delay-enter') || '100'
        );

        // 离开延时
        const delayLeave = parseInt(
          el.getAttribute('hover-delay-leave') || '0'
        );

        // 离开是否触发事件
        const watchLeave = el.getAttribute('hover-watch-leave') == 'true';

        const isHovered = useElementHover(el, { delayEnter, delayLeave });

        watch(isHovered, (v) => {
          if (watchLeave || v) {
            const res = binding.value(v);
            if (isPromise(res)) {
              res.catch(showError);
            }
          }
        });
      }
    }
  };

// el-cascader 数据转换
type ExtractPropertyNames<T, X> = {
  [K in keyof T]: T[K] extends X ? K : never;
}[keyof T];

export function toCascaderOptions<
  T extends Record<string, any>,
  V extends ExtractPropertyNames<T, CascaderNodeValue>,
  L extends ExtractPropertyNames<T, string>,
  C extends ExtractPropertyNames<T, T[] | undefined>
>(options: T[], value: V, label: L, children: C) {
  const convert = (option: T) =>
    toCascaderOption(option, value, label, children);

  return options.map(convert);
}

function toCascaderOption<
  T extends Record<string, any>,
  V extends ExtractPropertyNames<T, CascaderNodeValue>,
  L extends ExtractPropertyNames<T, string>,
  C extends ExtractPropertyNames<T, T[] | undefined>
>(option: T, value: V, label: L, children: C): CascaderOption {
  if (!option[children]) {
    return {
      value: option[value],
      label: option[label]
    };
  }

  const convert = (option: T) =>
    toCascaderOption(option, value, label, children);

  return {
    value: option[value],
    label: option[label],
    children: option[children].map(convert)
  };
}

// 唯一整数
let _uniqId = 0;
export function uniqId() {
  if (_uniqId == Number.MAX_SAFE_INTEGER) {
    return (_uniqId = 1);
  }

  return ++_uniqId;
}

// uuid生成
export const uuid = (function () {
  if (crypto.randomUUID) {
    return () => crypto.randomUUID();
  }

  return () =>
    '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: any) =>
      (
        c ^
        (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
      ).toString(16)
    );
})();
